/**
 * Utility functions for creating chart patterns like diagonal stripes
 */

/**
 * Creates a diagonal stripe pattern for Chart.js
 * @param {string} color - The base color for the stripes
 * @param {number} stripeWidth - Width of each stripe (default: 3)
 * @param {number} spacing - Spacing between stripes (default: 6)
 * @returns {CanvasPattern|string} Canvas pattern or fallback color
 */
export const createDiagonalStripePattern = (color, stripeWidth = 5, spacing = 5) => {
  if (typeof window === 'undefined' || !window.document) {
    // Return fallback color for SSR or non-browser environments
    return color;
  }

  try {
    // Create a large canvas element to draw the entire pattern without tiling
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Create a much larger canvas to minimize tiling artifacts
    // This ensures the pattern covers typical chart dimensions in one tile
    canvas.width = 1000;
    canvas.height = 1000;
    
    // Fill with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set stripe color
    ctx.fillStyle = color;
    
    // Calculate stripe parameters
    const totalSpacing = stripeWidth + spacing;
    
    // Draw diagonal stripes across the entire canvas
    // Start from far left to ensure full coverage
    const startX = -canvas.height; // Start far enough left to cover the diagonal
    const endX = canvas.width + canvas.height; // End far enough right
    
    for (let x = startX; x < endX; x += totalSpacing) {
      // Draw each stripe as a rotated rectangle
      ctx.save();
      
      // Calculate the center point for this stripe
      const centerX = x + canvas.height / 2;
      const centerY = canvas.height / 2;
      
      // Move to center, rotate, then draw
      ctx.translate(centerX, centerY);
      ctx.rotate(Math.PI / 4); // 45 degrees
      
      // Draw the stripe as a long rectangle
      ctx.fillRect(-stripeWidth / 2, -canvas.height, stripeWidth, canvas.height * 2);
      
      ctx.restore();
    }
    
    // Create pattern from the large canvas
    const pattern = ctx.createPattern(canvas, 'repeat');
    return pattern;
  } catch (error) {
    console.warn('Failed to create diagonal stripe pattern:', error);
    return color; // Fallback to solid color
  }
};

/**
 * Creates a background color or pattern based on design properties
 * @param {Object} design - Design object
 * @param {string} baseColor - Base color for the design
 * @param {boolean} useStripes - Whether to use stripes for reference designs
 * @returns {string|CanvasPattern} Color or pattern
 */
export const getDesignBackgroundColor = (design, baseColor, useStripes = true) => {
  if (design.is_reference && useStripes) {
    return createDiagonalStripePattern(baseColor);
  }
  return baseColor;
};

/**
 * Creates an array of background colors/patterns for multiple designs
 * @param {Array} designs - Array of design objects
 * @param {Array} colors - Array of base colors
 * @param {boolean} useStripes - Whether to use stripes for reference designs
 * @returns {Array} Array of colors/patterns
 */
export const getDesignBackgroundColors = (designs, colors, useStripes = true) => {
  return designs.map((design, index) => {
    const baseColor = colors[index % colors.length];
    return getDesignBackgroundColor(design, baseColor, useStripes);
  });
};

/**
 * Creates chart dataset with separate background and legend colors
 * @param {Array} designs - Array of design objects
 * @param {Array} colors - Array of base colors
 * @param {Object} baseDataset - Base dataset object
 * @param {boolean} useStripes - Whether to use stripes for reference designs
 * @returns {Object} Dataset with backgroundColor and legendColor
 */
export const createDatasetWithLegendColors = (designs, colors, baseDataset, useStripes = true) => {
  const baseColor = colors[0] || '#000000';
  
  return {
    ...baseDataset,
    backgroundColor: getDesignBackgroundColors(designs, colors, useStripes),
    // Store the solid base color for legend
    legendColor: baseColor,
  };
};
